import React, { useState, useEffect, useMemo } from "react";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFViewer,
  Font,
} from "@react-pdf/renderer";
import EditarOrdenForm from "./EditarOrdenForm";
import OpenRegular from "../fonts/OpenSans-Regular.ttf";
import OpenBold from "../fonts/OpenSans-Bold.ttf";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db, storage } from "../firebaseConfig";
import { ref, deleteObject } from "firebase/storage";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";

Font.register({
  family: "Open",
  fonts: [
    { src: OpenRegular, fontWeight: "normal" },
    { src: OpenBold, fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
    fontSize: 10,
    fontFamily: "Open",
  },
  pdfContainer: {
    position: "fixed",
    inset: 0, // equivale a top/right/bottom/left: 0
    padding: "2vw",
    backgroundColor: "rgba(0,0,0,0.6)",
    zIndex: 1000,
    display: "grid",
    placeItems: "center",
  },
  pdfViewer: {
    width: "min(96vw, 1200px)",
    height: "min(94dvh, 1000px)", // dvh = viewport dinámico (mejor en móviles)
    border: "1px solid #ccc",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  },
  closeButton: { color: "#fff", top: "15px", fontSize: "20px" },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  logoContainer: { flexDirection: "column", alignItems: "flex-start" },
  logo: { width: 120, marginBottom: 5 },
  orderTitleContainer: { alignItems: "flex-end" },
  orderTitle: {
    fontSize: 14,
    color: "#3579CB",
    fontWeight: "bold",
    letterSpacing: "1px",
  },
  orderNumber: {
    fontSize: 11,
    backgroundColor: "#FF0000",
    color: "#fff",
    fontWeight: "bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 2,
    letterSpacing: "0.5px",
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  infoLeft: { width: "45%" },
  infoRight: { width: "45%", alignItems: "flex-end" },
  infoTitle: {
    fontWeight: "bold",
    fontSize: 10,
    marginBottom: 10,
    textDecoration: "underline",
  },
  infoText: { fontSize: 9, marginBottom: 2 },
  proveedorNombre: { fontSize: 9, marginBottom: 2, fontWeight: "bold" },
  textoCondiciones: { fontSize: 9, marginBottom: 10, fontWeight: "bold" },
  infoTextRazon: { fontSize: 9, marginBottom: 2, fontWeight: "bold" },
  totalBox: {
    backgroundColor: "#3579CB",
    paddingHorizontal: 8,
    paddingVertical: 7,
    marginTop: 6,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalBoxText: { color: "#fff", fontWeight: "bold", letterSpacing: "0.5px" },
  labelTotal: {
    color: "#fff",
    fontWeight: "bold",
    textAlign: "right",
    width: "45%",
  },
  dataSection: { flexDirection: "row", marginBottom: 10, gap: 2 },
  dataBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#eaeaea",
    backgroundColor: "#eee",
    padding: 8,
    justifyContent: "center",
  },
  dataBoxTitle: {
    fontSize: 8,
    color: "#555",
    fontWeight: "semibold",
    textAlign: "center",
    letterSpacing: "0.1px",
  },
  dataBoxValue: {
    fontSize: 8,
    fontWeight: 600,
    marginTop: 2,
    textTransform: "uppercase",
    textAlign: "center",
    letterSpacing: "0.1px",
    minHeight: 10,
  },
  tableContainer: { marginTop: 10 },
  tableHeaderRow: { flexDirection: "row", backgroundColor: "#555" },
  tableHeaderCell: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    fontSize: 9,
    backgroundColor: "#555",
    color: "#fff",
    letterSpacing: "0.2px",
  },
  tableRow: { flexDirection: "row" },
  tableCell: {
    paddingVertical: 8,
    paddingHorizontal: 4,
    fontSize: 9,
    justifyContent: "center",
  },
  footerSection: {
    marginTop: 15,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  conditions: { width: "60%", fontSize: 9, lineHeight: 1.3 },
  totals: { width: "35%", fontSize: 9, minHeight: 100 },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 2,
  },
  totalLabel: {
    fontSize: 9,
    textAlign: "right",
    width: "45%",
    fontWeight: "bold",
  },
  totalValue: { fontSize: 9, paddingRight: 8 },
  numeroDePagina: { color: "#999999", fontSize: 9 },
  montoEnLetras: {
    fontSize: 9,
    marginTop: 0,
    textAlign: "right",
    fontWeight: "bold",
  },
  montoGroup: { alignItems: "flex-end", marginTop: 18, paddingRight: 0 },
  montoEnLetrasLabel: {
    fontFamily: "Open",
    fontWeight: "bold",
    fontSize: 9,
    textAlign: "right",
    color: "#666",
  },
});

/* ===== Helpers ===== */
const clean = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  const basura = /^(n\/a|na|n\.?d\.?|\.|-|s\/n|sn|null|undefined)$/i;
  return s && !basura.test(s) ? s : "";
};
const onlyDigits = (v) => (v || "").toString().replace(/\D+/g, "");

// Formato de dinero consistente: 1,234.56 (independiente del locale)
const formatMoney = (n) => {
  const x = (Number(n) || 0).toFixed(2);
  const [i, d] = x.split(".");
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + d;
};

// Normalización agresiva para nombres de empresa
const normBase = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Key de empresa: sin puntuación ni razón social
const empresaKey = (s = "") =>
  normBase(s)
    .replace(/[.,\-_/]/g, " ") // quita puntuación común
    .replace(/\b(s\.?\s*a\.?)\b/g, " ") // s.a.
    .replace(/\b(s\.?\s*de\s*r\.?\s*l\.?)\b/g, " ") // s. de r.l.
    .replace(/\b(c\.?\s*v\.?)\b/g, " ") // c.v.
    .replace(/\b(sa|cv|rl|de|del|la|el)\b/g, " ") // palabras vacías
    .replace(/\s+/g, " ")
    .trim();

const getNombreEmpresaProv = (p) =>
  p?.empresa || p?.nombre || p?.razonSocial || p?.Nombre || "";

// Lee los campos tal como guardas en proveedores
const getDireccionFieldsProv = (p = {}) => ({
  domicilio: clean(p.domicilio ?? p.calle ?? p.direccion?.calle),
  numeroExt: clean(p.numeroExt ?? p.noExterior ?? p.direccion?.numeroExt),
  numeroInt: clean(p.numeroInt ?? p.noInterior ?? p.direccion?.numeroInt),
  colonia: clean(p.colonia ?? p.direccion?.colonia),
  ciudad: clean(p.ciudad ?? p.direccion?.ciudad),
  estado: clean(p.estado ?? p.direccion?.estado),
  codigoPostal: onlyDigits(p.codigoPostal ?? p.cp ?? p.direccion?.codigoPostal),
  rfc: clean(p.rfc),
});

const direccionScore = (d) =>
  (d.domicilio ? 1 : 0) +
  (d.numeroExt ? 1 : 0) +
  (d.colonia ? 1 : 0) +
  (d.ciudad ? 1 : 0) +
  (d.estado ? 1 : 0) +
  (d.codigoPostal ? 1 : 0);

// Elegir canónico por nombre usando empresaKey()
const pickCanonicoPorNombre = (nombre, lista = []) => {
  const objetivo = empresaKey(nombre);
  const cand = lista.filter(
    (x) => empresaKey(getNombreEmpresaProv(x)) === objetivo
  );
  if (!cand.length) return null;
  const preferido = cand.find((x) => x.canonico || x.principal || x.preferido);
  if (preferido) return preferido;
  return (
    cand
      .map((c) => ({ c, f: getDireccionFieldsProv(c) }))
      .sort((a, b) => direccionScore(b.f) - direccionScore(a.f))[0]?.c ?? null
  );
};

/* ===== NÚMERO → TEXTO ===== */
const numeroATexto = (n) => {
  n = Math.floor(Math.abs(n));
  if (n === 0) return "CERO";
  const U = [
    "",
    "UNO",
    "DOS",
    "TRES",
    "CUATRO",
    "CINCO",
    "SEIS",
    "SIETE",
    "OCHO",
    "NUEVE",
  ];
  const E = {
    10: "DIEZ",
    11: "ONCE",
    12: "DOCE",
    13: "TRECE",
    14: "CATORCE",
    15: "QUINCE",
    16: "DIECISÉIS",
    17: "DIECISIETE",
    18: "DIECIOCHO",
    19: "DIECINUEVE",
  };
  const D = {
    20: "VEINTE",
    30: "TREINTA",
    40: "CUARENTA",
    50: "CINCUENTA",
    60: "SESENTA",
    70: "SETENTA",
    80: "OCHENTA",
    90: "NOVENTA",
  };
  const C = {
    100: "CIENTO",
    200: "DOSCIENTOS",
    300: "TRESCIENTOS",
    400: "CUATROCIENTOS",
    500: "QUINIENTOS",
    600: "SEISCIENTOS",
    700: "SETECIENTOS",
    800: "OCHOCIENTOS",
    900: "NOVECIENTOS",
  };
  const tens = (x) => {
    if (x < 10) return U[x];
    if (x <= 19) return E[x];
    if (x === 20) return "VEINTE";
    if (x < 30) {
      if (x === 22) return "VEINTIDÓS";
      if (x === 23) return "VEINTITRÉS";
      if (x === 26) return "VEINTISÉIS";
      return "VEINTI" + U[x - 20];
    }
    const de = Math.floor(x / 10) * 10;
    const un = x % 10;
    return D[de] + (un ? " Y " + U[un] : "");
  };
  const hundreds = (x) => {
    if (x === 100) return "CIEN";
    const h = Math.floor(x / 100) * 100;
    if (h) return C[h] + (x % 100 ? " " + tens(x % 100) : "");
    return tens(x);
  };
  let s = "";
  if (n >= 1_000_000) {
    const m = Math.floor(n / 1_000_000);
    s += m === 1 ? "UN MILLÓN" : `${numeroATexto(m)} MILLONES`;
    n %= 1_000_000;
    if (n) s += " ";
  }
  if (n >= 1000) {
    const k = Math.floor(n / 1000);
    s += k === 1 ? "MIL" : `${hundreds(k)} MIL`;
    n %= 1000;
    if (n) s += " ";
  }
  if (n > 0) s += hundreds(n);
  return s.trim();
};

const montoALetras = (monto, moneda = "PESOS", sufijo = "MXN") => {
  const totalCents = Math.round(Math.abs(monto) * 100);
  const entero = Math.floor(totalCents / 100);
  const cent = totalCents % 100;
  const fraccion = String(cent).padStart(2, "0");
  let literal = numeroATexto(entero);
  literal = literal.replace(/ UNO$/, " UN").replace(/ VEINTIUNO$/, " VEINTIÚN");
  literal = literal
    .replace(/ VEINTIUNO (?=MIL\b)/g, " VEINTIÚN ")
    .replace(/ UNO (?=MIL\b)/g, " UN ");
  const mon = moneda.toUpperCase();
  const monedaSingular =
    mon === "PESOS"
      ? "PESO"
      : mon === "DÓLARES" || mon === "DOLARES"
      ? "DÓLAR"
      : moneda;
  const usaDe = entero > 0 && entero % 1_000_000 === 0;
  const monedaFinal = entero === 1 ? monedaSingular : moneda;
  const monedaConDe = usaDe ? `DE ${monedaFinal}` : monedaFinal;
  return `${literal} ${monedaConDe} ${fraccion}/100 ${sufijo}`;
};

const ResumenOrden = ({
  orden,
  isOpen,
  onClose,
  proveedores,
  setOrdenes,
  ordenes,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [showAdjuntosModal, setShowAdjuntosModal] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const { formatDate } = useFunctionsPrevia();
  const [proveedorDeFS, setProveedorDeFS] = useState(null);

  useEffect(() => {
    const cls = "printing-overlay";
    if (pdfVisible) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [pdfVisible]);

  useEffect(() => {
    if (showAdjuntosModal && orden?.archivos) setAdjuntos(orden.archivos);
  }, [orden, showAdjuntosModal]);

  useEffect(() => {
    if (!isOpen && onClose) onClose();
  }, [isOpen, onClose]);

  // Respaldo: cargar proveedor por ID si no viene en props.proveedores
  useEffect(() => {
    const id = orden?.proveedor || orden?.proveedorId || orden?.proveedor__id;
    if (!id) {
      setProveedorDeFS(null);
      return;
    }
    const enProp =
      (proveedores || []).find((p) => p.__docId === id || p.id === id) || null;
    if (enProp) {
      setProveedorDeFS(enProp);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "proveedores", id));
        setProveedorDeFS(
          snap.exists() ? { __docId: snap.id, ...snap.data() } : null
        );
      } catch (e) {
        console.error("No pude leer el proveedor por id:", e);
        setProveedorDeFS(null);
      }
    })();
  }, [orden?.proveedor, orden?.proveedorId, orden?.proveedor__id, proveedores]);

  const handleDeleteArchivo = async (archivo) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Estás a punto de eliminar el archivo "${archivo.nombre}". Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      try {
        const fileRef = ref(storage, archivo.url);
        await deleteObject(fileRef);
        const ordenRef = doc(db, "ordenes", orden.id);
        await updateDoc(ordenRef, {
          archivos: orden.archivos.filter((a) => a.url !== archivo.url),
        });
        setAdjuntos((prev) => prev.filter((a) => a.url !== archivo.url));
        if (typeof setOrdenes === "function") {
          setOrdenes((prev) =>
            prev.map((c) =>
              c.id === orden.id
                ? {
                    ...c,
                    archivos: c.archivos.filter((a) => a.url !== archivo.url),
                  }
                : c
            )
          );
        }
        Swal.fire({
          icon: "success",
          title: "Archivo eliminado",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al eliminar el archivo:", error);
        Swal.fire({
          icon: "error",
          title: "Error al eliminar",
          text: "Hubo un problema al eliminar el archivo.",
        });
      }
    }
  };

  const handlePrint = () => setPdfVisible(true);
  const handleClosePDF = () => setPdfVisible(false);

  const memoizedPdfDocument = useMemo(() => {
    if (!orden || !orden.productosSeleccionados?.length) return null;

    // 1) Busca proveedor por ID
    const provById = Array.isArray(proveedores)
      ? proveedores.find(
          (p) => p.__docId === orden.proveedor || p.id === orden.proveedor
        )
      : null;

    // 2) Nombre mostrado
    const nombreProveedor =
      orden.nombreProveedor ||
      getNombreEmpresaProv(provById || proveedorDeFS) ||
      "N/D";

    // Universo de búsqueda
    const universo = [...(proveedores || []), proveedorDeFS].filter(Boolean);

    // 3) Fallback por RFC (si lo tenemos en algún lado)
    const rfcCandidate =
      (provById && provById.rfc) ||
      (proveedorDeFS && proveedorDeFS.rfc) ||
      (orden && orden.rfcProveedor) ||
      null;

    const provByRfc = rfcCandidate
      ? universo.find(
          (p) =>
            (p?.rfc || "").toString().trim().toUpperCase() ===
            rfcCandidate.toString().trim().toUpperCase()
        ) || null
      : null;

    // 4) Canónico por NOMBRE (empresaKey), luego RFC, luego por ID/FS
    const provCanonico =
      pickCanonicoPorNombre(nombreProveedor, universo) ||
      provByRfc ||
      provById ||
      proveedorDeFS ||
      {};

    // Dirección limpia
    const {
      domicilio,
      numeroExt,
      numeroInt,
      colonia,
      ciudad,
      estado,
      codigoPostal,
      rfc,
    } = getDireccionFieldsProv(provCanonico);

    // Líneas formateadas sin comas colgantes
    const linea1Parts = [];
    if (domicilio) {
      let s = domicilio;
      if (numeroExt) s += ` ${numeroExt}`;
      if (numeroInt) s += ` Int. ${numeroInt}`;
      linea1Parts.push(s);
    }
    if (colonia) linea1Parts.push(colonia);
    const linea1 =
      linea1Parts.join(", ") + (codigoPostal ? `, C.P. ${codigoPostal}` : "");
    const linea2 = [ciudad, estado].filter(Boolean).join(", ");

    // Paginación de productos
    const productosPorPagina = 25;
    const paginasDeProductos = [];
    for (
      let i = 0;
      i < orden.productosSeleccionados.length;
      i += productosPorPagina
    ) {
      paginasDeProductos.push(
        orden.productosSeleccionados.slice(i, i + productosPorPagina)
      );
    }

    // Totales
    const subtotal = orden.productosSeleccionados.reduce((acc, p) => {
      const tarifa = parseFloat(p.tarifa ?? 0) || 0;
      const cantidad = parseFloat(p.cantidad ?? 0) || 0;
      const precioBruto = tarifa * cantidad;
      let desc = 0;
      if (p.tipoDescuento === "%")
        desc = precioBruto * ((parseFloat(p.descuento ?? 0) || 0) / 100);
      else if (p.tipoDescuento === "$")
        desc = parseFloat(p.descuento ?? 0) || 0;
      return acc + (precioBruto - desc);
    }, 0);

    const descuentoPorcentaje = parseFloat(orden.descuento ?? 0) || 0;
    const descuentoAplicado = subtotal * (descuentoPorcentaje / 100);
    const subtotalConDescuento = subtotal - descuentoAplicado;
    const ivaTotal = +(subtotalConDescuento * 0.16).toFixed(2);
    const total = +(subtotalConDescuento + ivaTotal).toFixed(2);
    const divisa = (orden.divisa || "MXN").toUpperCase();
    const monedaPalabra = divisa === "USD" ? "DÓLARES" : "PESOS";
    const totalEnLetras = montoALetras(total, monedaPalabra, divisa);

    return (
      <Document>
        {paginasDeProductos.map((productosPagina, pageIndex) => (
          <Page key={pageIndex} size="A4" style={styles.page}>
            {/* Encabezado */}
            <View style={styles.headerContainer}>
              <View style={styles.logoContainer}>
                <Image src="/img/logo-iciamex2.png" style={styles.logo} />
              </View>
              <View style={styles.orderTitleContainer}>
                <Text style={styles.orderTitle}>ORDEN DE COMPRA</Text>
                <Text style={styles.orderNumber}>
                  #{orden.numeroOrden?.toString().padStart(4, "0")}
                </Text>
              </View>
            </View>

            <View style={{ marginBottom: 10 }}>
              <View
                style={{
                  borderTopWidth: 2,
                  borderTopColor: "#3579CB",
                  borderColor: "#000",
                  position: "relative",
                  borderWidth: "0.4px",
                  backgroundColor: "#3579CB",
                }}
              />
              <View
                style={{
                  position: "absolute",
                  top: -3,
                  left: 0,
                  right: 0,
                  height: 3,
                  backgroundColor: "#3579CB",
                  width: "25%",
                }}
              />
            </View>

            {/* Proveedor y Facturación */}
            <View style={styles.infoSection}>
              <View style={styles.infoLeft}>
                <Text style={styles.infoTitle}>PROVEEDOR:</Text>
                <Text style={styles.proveedorNombre}>{nombreProveedor}</Text>
                {!!linea1 && <Text style={styles.infoText}>{linea1}</Text>}
                {!!linea2 && (
                  <Text style={styles.infoText}>{linea2}, México.</Text>
                )}
                {!!rfc && <Text style={styles.infoText}>RFC: {rfc}</Text>}
              </View>

              <View style={styles.infoRight}>
                <Text style={styles.infoTitle}>FACTURACIÓN Y ENTREGA:</Text>
                <Text style={styles.infoTextRazon}>
                  UNIFICACION PARA LA AUTOMATIZACION
                </Text>
                <Text style={styles.infoText}>
                  LIC. RAMIRO HERNÁNDEZ 448, COL. LAS PINTITAS,
                </Text>
                <Text style={styles.infoText}>EL SALTO, JAL. C.P. 45693</Text>
                <Text style={styles.infoText}>RFC: UAU151222IT1</Text>
              </View>
            </View>

            {/* Datos Principales */}
            <View style={styles.dataSection}>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>FECHA:</Text>
                <Text style={styles.dataBoxValue}>
                  {formatDate(orden.fechaOrden)}
                </Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>COTIZACIÓN:</Text>
                <Text style={styles.dataBoxValue}>
                  {orden.cotizacion || "N/A"}
                </Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>INCOTERM:</Text>
                <Text style={styles.dataBoxValue}>{orden.incoterm || ""}</Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>DIVISA:</Text>
                <Text style={styles.dataBoxValue}>{orden.divisa}</Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>COND. PAGO:</Text>
                <Text style={styles.dataBoxValue}>{orden.condicion}</Text>
              </View>
              <View style={styles.dataBox}>
                <Text style={styles.dataBoxTitle}>COMPRADOR:</Text>
                <Text style={styles.dataBoxValue}>{orden.comprador}</Text>
              </View>
            </View>

            {/* Tabla de Productos */}
            <View style={styles.tableContainer}>
              <View style={styles.tableHeaderRow}>
                <Text
                  style={[
                    styles.tableHeaderCell,
                    { flex: 0.5, textAlign: "center" },
                  ]}
                >
                  PDA
                </Text>
                <Text
                  style={[
                    styles.tableHeaderCell,
                    { flex: 2, textAlign: "center" },
                  ]}
                >
                  NO. PARTE
                </Text>
                <Text
                  style={[
                    styles.tableHeaderCell,
                    { flex: 5, textAlign: "center" },
                  ]}
                >
                  DESCRIPCIÓN
                </Text>
                <Text
                  style={[
                    styles.tableHeaderCell,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  CANTIDAD
                </Text>
                <Text
                  style={[
                    styles.tableHeaderCell,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  PRECIO
                </Text>
                <Text
                  style={[
                    styles.tableHeaderCell,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  SUBTOTAL
                </Text>
              </View>

              {productosPagina.map((producto, index) => (
                <View
                  key={index}
                  style={[
                    styles.tableRow,
                    { backgroundColor: index % 2 === 0 ? "#fff" : "#EFF2F2" },
                  ]}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      { flex: 0.5, textAlign: "center" },
                    ]}
                  >
                    {index + 1 + pageIndex * productosPorPagina}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 2, textAlign: "center" }]}
                  >
                    {producto.modelo || producto.numeroDeParteCliente || ""}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 5, textAlign: "left" }]}
                  >
                    {producto.nombre || "Sin descripción"}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 1, textAlign: "center" }]}
                  >
                    {producto.cantidad}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 1, textAlign: "center" }]}
                  >
                    $
                    {(parseFloat(producto.tarifa ?? 0) || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 1, textAlign: "center" }]}
                  >
                    {(parseFloat(producto.importe ?? 0) || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>
                </View>
              ))}
            </View>

            <View>
              <View
                style={{
                  border: "0.5px solid #3579CB",
                  backgroundColor: "#3579CB",
                  marginTop: 0,
                }}
              />
            </View>

            {/* Totales en la última página */}
            {pageIndex === paginasDeProductos.length - 1 && (
              <>
                <View style={styles.footerSection}>
                  <View style={styles.conditions}>
                    <Text style={styles.textoCondiciones}>
                      CONDICIONES / OBSERVACIONES:
                    </Text>
                    <Text>{orden.notas}</Text>
                  </View>
                  <View style={styles.totals}>
                    <View style={styles.totalRow}>
                      <Text style={styles.totalLabel}>Subtotal</Text>
                      <Text style={styles.totalValue}>
                        ${formatMoney(subtotal)}
                      </Text>
                    </View>

                    {descuentoAplicado > 0 && (
                      <>
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>
                            Descuento ({descuentoPorcentaje}%)
                          </Text>
                          <Text style={styles.totalValue}>
                            -${formatMoney(descuentoAplicado)}
                          </Text>
                        </View>
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>Restante</Text>
                          <Text style={styles.totalValue}>
                            ${formatMoney(subtotalConDescuento)}
                          </Text>
                        </View>
                      </>
                    )}

                    <View style={styles.totalRow}>
                      <Text style={styles.totalLabel}>IVA</Text>
                      <Text style={styles.totalValue}>
                        ${formatMoney(ivaTotal)}
                      </Text>
                    </View>

                    <View style={[styles.totalRow, styles.totalBox]}>
                      <Text style={[styles.labelTotal]}>TOTAL {divisa}</Text>
                      <Text style={[styles.totalBoxText]}>
                        ${formatMoney(total)}
                      </Text>
                    </View>

                    <View style={styles.montoGroup}>
                      <Text style={styles.montoEnLetrasLabel}>
                        IMPORTE TOTAL CON LETRA
                      </Text>
                      <Text wrap={false} style={styles.montoEnLetras}>
                        {totalEnLetras}
                      </Text>
                    </View>
                  </View>
                </View>
              </>
            )}

            {/* Footer fijo */}
            {pageIndex === paginasDeProductos.length - 1 && (
              <>
                <View
                  style={{
                    flex: 1,
                    justifyContent: "flex-end",
                    marginBottom: 10,
                  }}
                  wrap={false}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <View
                      style={{ width: "50%", marginRight: 10, marginTop: 40 }}
                    >
                      <Text style={{ fontSize: 9 }}>
                        Para cualquier duda, comunicarse al teléfono:
                      </Text>
                      <Text style={{ fontSize: 9 }}>
                        33 2192 8621 / 33 3619 2439 ó al correo{" "}
                        <Text
                          style={{ textDecoration: "underline", fontSize: 9 }}
                        >
                          victor@iciamex.com
                        </Text>
                      </Text>
                    </View>
                    <View style={{ width: "50%", alignItems: "flex-end" }}>
                      <Image
                        src="/img/firma.png"
                        style={{ width: 70, height: "auto" }}
                      />
                      <View
                        style={{
                          border: "1px solid #000",
                          borderWidth: "0.4px",
                          width: "30%",
                          marginTop: 10,
                        }}
                      />
                      <Text
                        style={{
                          fontSize: 9,
                          fontWeight: "bold",
                          marginTop: 5,
                          textAlign: "right",
                        }}
                      >
                        Víctor García
                      </Text>
                      <Text style={{ fontSize: 9, textAlign: "right" }}>
                        Proyectos
                      </Text>
                    </View>
                  </View>
                </View>

                <View
                  fixed
                  style={{
                    position: "absolute",
                    bottom: 15,
                    left: 30,
                    right: 30,
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTopWidth: 0.5,
                    borderTopColor: "#999999",
                    paddingTop: 5,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "left",
                      marginLeft: 0,
                      color: "#999999",
                    }}
                  >
                    UNIFICACION PARA LA AUTOMATIZACION SA DE CV
                  </Text>
                  <Text
                    style={styles.numeroDePagina}
                    render={({ pageNumber }) => `${pageNumber}`}
                  />
                </View>
              </>
            )}
          </Page>
        ))}
      </Document>
    );
  }, [orden, proveedores, proveedorDeFS, formatDate]);

  useEffect(() => {
    let isMounted = true;
    let url;
    const generateBlobUrl = async () => {
      if (!memoizedPdfDocument) {
        setPdfBlobUrl(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      const blob = await pdf(memoizedPdfDocument).toBlob();
      if (isMounted) {
        url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setLoading(false);
      }
    };
    generateBlobUrl();
    return () => {
      isMounted = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [memoizedPdfDocument]);

  if (pdfVisible) {
    return (
      <div style={styles.pdfContainer}>
        <button
          className="cerrar-button"
          style={styles.closeButton}
          onClick={handleClosePDF}
        >
          <i className="bi bi-x-lg"></i>
        </button>
        <PDFViewer className="pdf-viewer" style={styles.pdfViewer}>
          {memoizedPdfDocument}
        </PDFViewer>
      </div>
    );
  }

  const handleEdit = () => {
    setEditMode(true);
    setShowSummary(false);
  };
  const closeModal = () => {
    setEditMode(false);
    setShowSummary(true);
    onClose();
  };

  if (editMode) {
    return (
      <EditarOrdenForm
        orden={orden}
        proveedores={proveedores}
        productos={orden && orden.productosSeleccionados}
        setOrdenes={setOrdenes}
        ordenes={ordenes}
        onClose={closeModal}
      />
    );
  }

  if (showSummary && orden && orden.productosSeleccionados) {
    return (
      <div className="resumen-cotizacion-container">
        {/* Header de acciones: izquierda = Regresar / derecha = botones azules */}
        <div className="resumen-cotizacion-header">
          <button
            className="back-resumen-btn"
            onClick={onClose}
            title="Regresar a la tabla"
          >
            <i className="bi bi-arrow-left-short"></i>
            <span>Regresar a la tabla</span>
          </button>

          <div className="resumen-cotizacion-actions">
            <div className="dropdown">
              <button className="dropbtn">
                <i className="bi bi-printer iconResumen"></i> Imprimir / PDF
              </button>

              <div className="dropdown-content">
                <button onClick={handlePrint}>
                  <i
                    className="bi bi-file-earmark-text"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Imprimir
                </button>

                <a
                  href={pdfBlobUrl || "#"}
                  download={`${orden.numeroOrden || "sin_numero"}_${
                    orden.nombreProveedor || "sin_nombre"
                  }.pdf`}
                  style={{ textDecoration: "none" }}
                  onClick={(e) => {
                    if (!pdfBlobUrl) e.preventDefault();
                  }}
                >
                  <button>
                    <i
                      className="bi bi-download"
                      style={{ marginRight: "10px" }}
                    ></i>
                    Descargar PDF
                  </button>
                </a>
              </div>
            </div>
            <button>
              <i className="bi bi-envelope-fill iconResumen"></i> Correo
            </button>

            <button onClick={handleEdit}>
              <i className="bi bi-pencil-fill iconResumen"></i> Editar
            </button>

            <button>
              <i className="bi bi-receipt iconResumen"></i> Facturar
            </button>

            {orden.archivos && orden.archivos.length > 0 && (
              <button
                onClick={() => setShowAdjuntosModal(true)}
                style={{
                  margin: 0,
                  border: "1px solid #d0d7de",
                  borderRadius: 8,
                }}
              >
                <i className="bi bi-paperclip iconResumen"></i> Archivos
                Adjuntos
              </button>
            )}
          </div>
        </div>

        <Modal
          isOpen={showAdjuntosModal}
          onRequestClose={() => setShowAdjuntosModal(false)}
          contentLabel="Archivos Adjuntos"
          style={{
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              width: "35%",
              maxHeight: "80vh",
              overflowY: "auto",
              padding: "20px",
              borderRadius: "10px",
            },
            overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          }}
        >
          <button
            onClick={() => setShowAdjuntosModal(false)}
            className="modal-close"
            title="Cerrar"
          >
            <i className="bi bi-x-lg"></i>
          </button>

          <h3
            style={{
              marginBottom: "20px",
              color: "#000",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Archivos Adjuntos
          </h3>

          <ul style={{ listStyle: "none", padding: 0 }}>
            {adjuntos.map((archivo, index) => {
              const ext = (archivo.nombre || "").split(".").pop().toLowerCase();
              const iconMap = {
                xls: ["bi-file-earmark-excel", "green"],
                xlsx: ["bi-file-earmark-excel", "green"],
                csv: ["bi-file-earmark-excel", "green"],
                doc: ["bi-file-earmark-word", "blue"],
                docx: ["bi-file-earmark-word", "blue"],
                pdf: ["bi-file-earmark-pdf", "red"],
                jpg: ["bi-image", "blue"],
                jpeg: ["bi-image", "blue"],
                png: ["bi-image", "blue"],
                svg: ["bi-image", "blue"],
              };
              const [icon, color] = iconMap[ext] || ["bi-file-earmark", "gray"];

              return (
                <li
                  key={index}
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className={`bi ${icon}`}
                      style={{ color, fontSize: "1.5rem", marginRight: "10px" }}
                    />
                    <a
                      href={archivo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "#000",
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {archivo.nombre}
                    </a>
                  </div>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <a href={archivo.url} download={archivo.nombre}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          const link = document.createElement("a");
                          link.href = archivo.url;
                          link.target = "_blank";
                          link.rel = "noopener noreferrer";
                          link.download = archivo.nombre;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#007bff",
                          cursor: "pointer",
                          fontSize: "20px",
                        }}
                        title="Descargar archivo"
                      >
                        <i className="bi bi-download"></i>
                      </button>
                    </a>
                    <button
                      onClick={() => handleDeleteArchivo(archivo)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        marginRight: "10px",
                        fontSize: "20px",
                      }}
                      title="Eliminar archivo"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Modal>

        <div
          style={{
            height: "clamp(360px, calc(100dvh - 220px), 900px)",
            width: "100%",
            marginTop: "50px",
            display: "grid",
            placeItems: "center",
          }}
        >
          {loading ? (
            <div className="loader2"></div>
          ) : (
            pdfBlobUrl && (
              <iframe
                src={`${pdfBlobUrl}#toolbar=0`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  borderRadius: 4,
                  boxShadow: "0 6px 20px rgba(0,0,0,.15)",
                }}
                frameBorder="0"
                title="Vista previa del PDF"
              />
            )
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default ResumenOrden;
